// src/store/taskSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { TaskType } from "../../components/Tasks/constants";

interface TaskState {
  tasks: TaskType[];
  loading: boolean;
  error: string | null;
  selectedTask: TaskType | null;
  showTaskModal: boolean;
  isDeleting: boolean;
}

const initialState: TaskState = {
  tasks: [],
  loading: false,
  error: null,
  selectedTask: null,
  showTaskModal: false,
  isDeleting: false,
};

export const fetchTasks = createAsyncThunk<TaskType[]>(
  "tasks/fetchTasks",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_SPL_URL}/v1/tradegpt/task-schedules`, { withCredentials: true });
      return data.data;
    }
    catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const fetchTask = createAsyncThunk<TaskType, string, { rejectValue: string } >(
  "tasks/fetchTask",
  async (taskId: string, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_SPL_URL}/v1/tradegpt/task-schedules/${taskId}`, { withCredentials: true });
      return data.data;
    }
    catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const createTask = createAsyncThunk(
  "tasks/createTask",
  async (task: Partial<TaskType>, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_SPL_URL}/v1/tradegpt/task-schedules`,
        task,
        {
          withCredentials: true,
        },
      );
      return res.data;
    }
    catch (err) {
      return rejectWithValue(err.response?.data || "Failed to add task");
    }
  },
);

export const updateTask = createAsyncThunk(
  "tasks/updateTask",
  async (arg: { task: Partial<TaskType> }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${process.env.REACT_APP_SPL_URL}/v1/tradegpt/task-schedules/${arg.task.id}`,
        arg.task,
        { withCredentials: true });
      return res.data;
    }
    catch (err) {
      return rejectWithValue(err.response?.data || "Failed to update task");
    }
  },
);

export const deleteTask = createAsyncThunk(
  "tasks/deleteTask",
  async (taskId: string, { rejectWithValue }) => {
    try {
      await axios.delete(`${process.env.REACT_APP_SPL_URL}/v1/tradegpt/task-schedules/${taskId}`, { withCredentials: true });
      return taskId;
    }
    catch (err) {
      return rejectWithValue(err.response?.data || "Failed to add task");
    }
  },
);

const taskSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    setShowTaskModal(state, action: PayloadAction<boolean>) {
      state.showTaskModal = action.payload;
      if (action.payload === false) {
        state.selectedTask = null;
      }
    },
    setSelectedTask(state, action: PayloadAction<TaskType | null>) {
      state.selectedTask = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchTasks
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action: PayloadAction<TaskType[]>) => {
        state.loading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex(task => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })
      .addCase(updateTask.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.tasks.unshift(action.payload);
      })
      .addCase(createTask.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteTask.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.error = null;
        state.tasks = state.tasks.filter(task => task.id !== action.payload);
      })
      .addCase(deleteTask.rejected, (state) => {
        state.isDeleting = false;
      });
  },
});

export const { setShowTaskModal, setSelectedTask } = taskSlice.actions;
export default taskSlice.reducer;
